import type { Beschikbaar, PeriodeKeuze } from '@/lib/periode';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const MAANDNAMEN = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

const maandLabel = (periode: string) => {
  const [jaar, maand] = periode.split('-');
  return `${MAANDNAMEN[Number(maand) - 1]} ${jaar}`;
};

const KNOP: Record<PeriodeKeuze, string> = {
  maand: 'Maand',
  jaar: 'Jaar',
  altijd: 'Altijd',
};

export interface PeriodeKiezerProps<S extends PeriodeKeuze> {
  soort: S;
  /** Welke knoppen er staan. Het overzicht kiest altijd een periode; de
   *  hulpvragen mogen ook alles bij elkaar tellen. */
  soorten: readonly S[];
  waarde: string;
  beschikbaar: Beschikbaar;
  onSoort: (soort: S) => void;
  onWaarde: (waarde: string) => void;
}

/**
 * Schakelaar maand/jaar met daarnaast welke maand of welk jaar. Een paar opties,
 * dus een `ToggleGroup`; de lijst zelf kan lang worden en is daarom een
 * `Select`.
 *
 * `ToggleGroup type="single"` geeft een lege string terug als je de actieve
 * knop nog eens aanklikt. Zonder die te negeren zou het scherm dan zonder
 * periode komen te staan.
 */
export function PeriodeKiezer<S extends PeriodeKeuze>({
  soort,
  soorten,
  waarde,
  beschikbaar,
  onSoort,
  onWaarde,
}: PeriodeKiezerProps<S>) {
  const opties =
    soort === 'maand' ? beschikbaar.maanden : soort === 'jaar' ? beschikbaar.jaren : [];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ToggleGroup
        onValueChange={(v) => v && onSoort(v as S)}
        size="sm"
        type="single"
        value={soort}
        variant="outline"
      >
        {soorten.map((s) => (
          <ToggleGroupItem key={s} value={s}>
            {KNOP[s]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {/* Bij alles bij elkaar valt er niets te kiezen. */}
      {soort !== 'altijd' && (
        <Select onValueChange={onWaarde} value={waarde}>
          <SelectTrigger aria-label="Kies een periode" className="w-44" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {opties.map((p) => (
                <SelectItem key={p} value={p}>
                  {soort === 'maand' ? maandLabel(p) : p}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
